import fs from 'node:fs/promises';
import crypto from 'node:crypto';

import type { Request, Response } from 'express';
import type { OpenapiCache } from './types';
import type { MiddlewareContext } from '../types';

const MAX_REF_DEPTH = 5;

const EMPTY_OPENAPI_SPEC = {
  openapi: '3.0.0',
  info: { title: '开放 API', version: '0.0.0' },
  paths: {},
  components: { schemas: {} },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpec = Record<string, any>;

export function createOpenapiSpecHandler(openapiSpecFilePath: string) {
  let cache: OpenapiCache | null = null;

  return async (_req: Request, res: Response, context: MiddlewareContext) => {
    try {
      let fileBuffer: string;
      try {
        fileBuffer = await fs.readFile(openapiSpecFilePath, 'utf-8');
      } catch (err) {
        if ((err as NodeJS.ErrnoException)?.code === 'ENOENT') {
          return res.json(EMPTY_OPENAPI_SPEC);
        }
        throw err;
      }
      const currentHash = crypto.createHash('md5').update(fileBuffer).digest('hex');

      if (cache && cache.fileHash === currentHash) {
        return res.json(cache.data);
      }

      let parsed: AnySpec;
      try {
        parsed = JSON.parse(fileBuffer);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid JSON';
        return res.status(500).json({
          error: 'Failed to load OpenAPI spec',
          message: `${openapiSpecFilePath} invalid: ${message}`,
        });
      }

      if (!parsed || typeof parsed !== 'object' || !parsed.paths || typeof parsed.paths !== 'object') {
        return res.status(500).json({
          error: 'Failed to load OpenAPI spec',
          message: `${openapiSpecFilePath} missing "paths"`,
        });
      }

      const result = buildFilteredSpec(parsed, context.basePath ?? '');

      cache = {
        data: result,
        fileHash: currentHash,
      };

      res.json(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      res.status(500).json({
        error: 'Failed to load OpenAPI spec',
        message,
      });
    }
  };
}

function buildFilteredSpec(spec: AnySpec, globalPrefix: string) {
  const schemas = (spec.components?.schemas ?? {}) as Record<string, unknown>;
  const paths = (spec.paths ?? {}) as Record<string, AnySpec>;
  const filteredPaths: Record<string, Record<string, unknown>> = {};

  const cleanPrefix = globalPrefix.replace(/\/+$/, '');

  for (const [pathKey, methods] of Object.entries(paths)) {
    const cleanPath = stripPrefix(pathKey, cleanPrefix);
    if (!cleanPath.startsWith('/openapi')) continue;

    filteredPaths[cleanPath] = {};

    for (const [method, operation] of Object.entries(methods as Record<string, unknown>)) {
      if (['parameters', 'summary', 'description', 'servers'].includes(method)) continue;
      filteredPaths[cleanPath][method] = resolveRefsDeep(structuredClone(operation), schemas);
    }
  }

  return {
    openapi: spec.openapi ?? '3.0.0',
    paths: filteredPaths,
    components: {
      schemas: resolveAllSchemaRefs(structuredClone(schemas)),
    },
  };
}

function stripPrefix(pathStr: string, prefix: string): string {
  if (prefix && pathStr.startsWith(prefix)) {
    return pathStr.slice(prefix.length);
  }
  return pathStr;
}

function resolveRefsDeep(
  obj: unknown,
  schemas: Record<string, unknown>,
  depth = 0,
): unknown {
  if (depth > MAX_REF_DEPTH || obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => resolveRefsDeep(item, schemas, depth));
  }

  const record = obj as Record<string, unknown>;

  if (typeof record['$ref'] === 'string') {
    const resolved = resolveRef(record['$ref'] as string, schemas);
    if (resolved !== undefined) {
      return resolveRefsDeep(structuredClone(resolved), schemas, depth + 1);
    }
    return record;
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    result[key] = resolveRefsDeep(value, schemas, depth);
  }
  return result;
}

function resolveRef(ref: string, schemas: Record<string, unknown>): unknown {
  const prefix = '#/components/schemas/';
  if (ref.startsWith(prefix)) {
    const name = ref.slice(prefix.length);
    return schemas[name];
  }
  return undefined;
}

function resolveAllSchemaRefs(
  schemas: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [name, schema] of Object.entries(schemas)) {
    result[name] = resolveRefsDeep(schema, schemas);
  }
  return result;
}
